export const EVENT_TYPE = {
  breedGuideResults: "breedGuideResults",
  breedGuideResultSelected: "breedGuideResultSelected",
  campaignFormSubmitted: "CampaignForm submitted",
  campaignHybridFormSubmitted: "form_success",
  campaignPopupClick: "CampaignPopup clicked",
  event: "event",
  onlineBooking: "onlineBooking",
  trafficSource: "traffic_source",

  // NEW EVENT TYPES
  formInteraction: "form_interaction",
  linkClick: "link_click"
} as const;

export const CONTENT_TYPE = {
  content: "content",
  footer: "footer",
  hero: "hero",
  list: "list",
  nav: "nav"
} as const;

export const LINK_TYPE = {
  bookingService: "booking_service",
  clinic: "clinic",
  crossBrand: "cross_brand",
  directions: "directions",
  email: "email",
  emergency: "emergency",
  other: "other",
  phone: "phone",
  referral: "referral",
  sameSite: "same_site",
  social: "social",
  treatment: "treatment"
} as const;

// TODO:
// Add target_type clinic, treatment, emergency

export const LINK_SERVICE_TYPE = {
  // PMS
  abuPet: "AbuPet",
  petLeo: "PetLEO",
  petsXL: "petsXL",
  provet: "ProVet",
  sanimalis: "Sanimalis",
  vetstoria: "Vetstoria",

  // Social media
  linkedIn: "LinkedIn",
  facebook: "Facebook",
  instagram: "Instagram",
  x: "X (formerly Twitter)",

  other: "Other"
} as const;

export const FORM_ACTION = {
  start: "start",
  step: "step",
  submit: "submit"
} as const;

export const FORM_TYPE = {
  campaign: "campaign",
  consent: "consent",
  contact: "contact",
  other: "other",
  referral: "referral",
  termsAndConditions: "terms_and_conditions"
} as const;

export type ContentType = (typeof CONTENT_TYPE)[keyof typeof CONTENT_TYPE];

export type EventType = (typeof EVENT_TYPE)[keyof typeof EVENT_TYPE];

export type FormAction = (typeof FORM_ACTION)[keyof typeof FORM_ACTION];

export type FormType = (typeof FORM_TYPE)[keyof typeof FORM_TYPE];

export type LinkType = (typeof LINK_TYPE)[keyof typeof LINK_TYPE];

export type LinkServiceType = (typeof LINK_SERVICE_TYPE)[keyof typeof LINK_SERVICE_TYPE];

export type FormInteractionEventParameters = {
  content_id?: string;
  content_type: ContentType | string;
  form_action: FormAction;
  form_id?: string;
  form_name?: string;
  form_type: FormType;

  /**
   * Internal campaign name (e.g. "Always-on", "Dermatology"), set on clinic campaign landing pages
   * so multiple campaigns on the same clinic are distinguishable.
   */
  campaign_name?: string;
};

export type LinkEventParameters = {
  // Shared parameters
  /**
   * The type of the content.
   * For example, if the link is in the main navigation, this would be "nav".
   * If it's in the footer, it would be "footer". If it's in a specific block, it could be the content type of that block.
   */
  content_type: ContentType | string;

  /**
   * Indicates whether the click is expected to lead to a conversion (e.g. booking, referral, email or phone link)
   */
  is_conversion: boolean;

  /**
   * Analytics type of the link.
   */
  target_type: LinkType;

  /**
   * Link href.
   */
  target_url: string;

  // Parameters only applicable for some link types

  // Parameters if the link is related to a clinic (e.g. clinic-specific booking or referral link).
  /**
   * Clinic business id
   */
  clinic_bid?: string;

  /**
   * Clinic CMS id
   */
  clinic_cms_id?: string;

  /**
   * Clinic name
   */
  clinic_name?: string;

  /**
   * Clinic PMS id
   */
  clinic_pms_id?: string;

  /**
   * Content id of the containing block, if any.
   */
  content_id?: string;

  /**
   * Content display name of the containing block, if any.
   */
  content_name?: string;

  /**
   * Type of the booking service, if the link is a booking service link.
   */
  target_service?: LinkServiceType;

  /**
   * Treatment id, if the link is related to a treatment (e.g. treatment-specific booking link).
   * Unused for now.
   */
  treatment_ccid?: string;

  /**
   * Text of the link.
   */
  text: string;

  /**
   * Booking session id, set on booking button clicks.
   */
  form_session_id?: string;

  /**
   * UTM channel from the user's original traffic source.
   */
  utm_channel?: string;

  /**
   * UTM medium from the user's original traffic source.
   */
  utm_medium?: string;

  /**
   * UTM source from the user's original traffic source.
   */
  utm_source?: string;

  /**
   * Internal campaign name (e.g. "Always-on", "Dermatology"), set on clinic campaign landing pages
   * so multiple campaigns on the same clinic are distinguishable.
   */
  campaign_name?: string;
};
